import type { GridColumnMetadata } from "../types";
import { snakeToCamel } from "../utils/fieldMapping";

/**
 * Configuration for grid columns.
 *
 * The system is fully dynamic - all metadata comes from Aras API.
 * This config only provides:
 * - exclude: Fields to hide from the grid (system fields)
 * - override: Column width/style overrides (optional)
 *
 * Field name conversion is automatic via snakeToCamel().
 */

export interface GridColumnConfig {
  exclude?: string[];
  override?: Record<string, Partial<GridColumnMetadata>>;
  /** Columns to show in the ItemPickerModal (by Aras property name, snake_case). Overrides default first-N selection. */
  pickerColumns?: string[];
  /** Item-reference columns (snake_case) that should have dropdown filters on the list page. */
  filterableItemRefs?: string[];
}

// Common system fields to exclude from all grids
const commonExclude = [
  "id",
  "created_by_id",
  "modified_by_id",
  "owned_by_id",
  "config_id",
  "permission_id",
  "team_id",
  "locked_by_id",
  "generation",
  "is_current",
  "major_rev",
  "minor_rev",
];

// Grid column configuration — applies generically to all item types
export const gridColumnConfig: Record<string, GridColumnConfig> = {
  _default: {
    exclude: commonExclude,
    override: {
      notes: { width: 200 },
      description: { width: 200 },
    },
  },
  Student: {
    exclude: commonExclude,
    pickerColumns: [
      "full_name",
      "birth_year",
      "school_class",
      "school_year",
      "facility_id",
      "parent_name",
    ],
    filterableItemRefs: ["facility_id"],
  },
  Class: {
    exclude: commonExclude,
    pickerColumns: [
      "education_facility_id",
      "school_year",
      "class_name",
      "lead_coach_id",
      "class_status",
    ],
    filterableItemRefs: ["education_facility_id"],
  },
};

/**
 * Get configured picker columns for an entity type (if any).
 * Returns camelCase field names, or undefined if no picker config exists.
 */
export const getPickerColumns = (entityType: string): string[] | undefined => {
  const config = gridColumnConfig[entityType];
  if (!config?.pickerColumns) return undefined;
  return config.pickerColumns.map(snakeToCamel);
};

/**
 * Get configured filterable item-reference column names for an entity type.
 * Returns camelCase field names, or empty array if none configured.
 */
export const getFilterableItemRefs = (entityType: string): string[] => {
  const config = gridColumnConfig[entityType];
  if (!config?.filterableItemRefs) return [];
  return config.filterableItemRefs.map(snakeToCamel);
};

/**
 * Get columns with automatic field name conversion and config applied.
 * Converts Aras property names (snake_case) to API field names (camelCase).
 */
export const getColumnsWithOverrides = (
  columns: GridColumnMetadata[],
  entityType: string,
): GridColumnMetadata[] => {
  // Get config for this entity type, or use default
  const config =
    gridColumnConfig[entityType] || gridColumnConfig._default || {};

  // Filter out excluded columns
  const excludeSet = new Set(config.exclude || commonExclude);
  let result = columns.filter((col) => !excludeSet.has(col.name));

  // Convert snake_case to camelCase automatically
  result = result.map((col) => ({
    ...col,
    name: snakeToCamel(col.name),
  }));

  // Apply overrides (using the camelCase name)
  if (config.override) {
    result = result.map((col) => ({
      ...col,
      ...(config.override?.[col.name] || {}),
    }));
  }

  return result;
};
